// Empire Apps Chassis · identity-zkp · did-resolver
// Resolves did:key (no network) and did:web (well-known fetch + cache).

import type { DIDDocument, DIDString, Ed25519PublicKey } from './types.ts';
import { buildDIDDocument, multibaseToPublicKey, parseDID } from './did.ts';

interface CacheEntry {
  doc: DIDDocument;
  fetchedAt: number;
  ttlMs: number;
}

const cache = new Map<string, CacheEntry>();
const DEFAULT_TTL_MS = 5 * 60 * 1000;
const FETCH_TIMEOUT_MS = 4000;

export interface DidResolveOk {
  readonly ok: true;
  readonly doc: DIDDocument;
  readonly publicKey: Ed25519PublicKey;
  readonly fromCache: boolean;
}

export interface DidResolveErr {
  readonly ok: false;
  readonly reason: 'unsupported-method' | 'fetch-failed' | 'malformed-doc' | 'no-key';
}

export async function resolveDid(did: DIDString): Promise<DidResolveOk | DidResolveErr> {
  const cached = cache.get(did);
  if (cached && Date.now() - cached.fetchedAt < cached.ttlMs) {
    const pk = extractPublicKey(cached.doc);
    if (!pk) return { ok: false, reason: 'no-key' };
    return { ok: true, doc: cached.doc, publicKey: pk, fromCache: true };
  }

  const { method, identifier } = parseDID(did);

  if (method === 'key') {
    try {
      const pk = multibaseToPublicKey(identifier);
      const doc = buildDIDDocument(did, pk);
      cache.set(did, { doc, fetchedAt: Date.now(), ttlMs: DEFAULT_TTL_MS });
      return { ok: true, doc, publicKey: pk, fromCache: false };
    } catch {
      return { ok: false, reason: 'malformed-doc' };
    }
  }

  if (method === 'web') {
    const url = didWebToUrl(identifier);
    try {
      const ctl = new AbortController();
      const t = setTimeout(() => ctl.abort(), FETCH_TIMEOUT_MS);
      const res = await fetch(url, { signal: ctl.signal, headers: { accept: 'application/did+json' } });
      clearTimeout(t);
      if (!res.ok) return { ok: false, reason: 'fetch-failed' };
      const doc = (await res.json()) as DIDDocument;
      const pk = extractPublicKey(doc);
      if (!pk) return { ok: false, reason: 'no-key' };
      cache.set(did, { doc, fetchedAt: Date.now(), ttlMs: DEFAULT_TTL_MS });
      return { ok: true, doc, publicKey: pk, fromCache: false };
    } catch {
      return { ok: false, reason: 'fetch-failed' };
    }
  }

  return { ok: false, reason: 'unsupported-method' };
}

function didWebToUrl(identifier: string): string {
  const decoded = identifier.replace(/%3A/g, ':');
  const parts = decoded.split(':');
  const host = parts[0] as string;
  const path = parts.slice(1).join('/') || '.well-known';
  return `https://${host}/${path}/did.json`;
}

function extractPublicKey(doc: DIDDocument): Ed25519PublicKey | null {
  for (const vm of doc.verificationMethod) {
    if (vm.type === 'Ed25519VerificationKey2020' && vm.publicKeyMultibase) {
      try {
        return multibaseToPublicKey(vm.publicKeyMultibase);
      } catch {
        continue;
      }
    }
    if (vm.publicKeyJwk) {
      const jwk = vm.publicKeyJwk as { kty?: string; crv?: string; x?: string };
      if (jwk.kty === 'OKP' && jwk.crv === 'Ed25519' && jwk.x) {
        return decodeJwkXEd25519(jwk.x);
      }
    }
  }
  return null;
}

function decodeJwkXEd25519(x: string): Ed25519PublicKey {
  const pad = x.length % 4 === 0 ? '' : '='.repeat(4 - (x.length % 4));
  const b64 = (x + pad).replace(/-/g, '+').replace(/_/g, '/');
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out as Ed25519PublicKey;
}

export function clearDidCache(): void {
  cache.clear();
}
